/**
 * Hook para gerenciar a lógica do input de chave NFe
 */

"use client";
import { useState, useEffect, useCallback, useRef } from "react";
import { usePrenotaStore } from "@PN/inclusao";
import { identificacaoUtils } from "../../_utils";

export function useChaveInput(onAutoSearch?: (chave: string) => void) {
  const chaveNFe = usePrenotaStore((s) => s.draft.documento.chaveNFe);
  const [inputValue, setInputValue] = useState(chaveNFe || "");
  const debounceTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Sincroniza o input com a store apenas quando a store muda externamente
  useEffect(() => {
    setInputValue(chaveNFe || "");
  }, [chaveNFe]);

  // Cleanup do timeout ao desmontar
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Handler para mudança do input com debounce
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value || "";

      // Limita o tamanho máximo (permite caracteres especiais)
      if (newValue.length <= 70) {
        // Atualiza o input imediatamente (sem delay)
        setInputValue(newValue);

        // Limpa timeout anterior
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Debounce para atualizar a store
        debounceTimeoutRef.current = setTimeout(() => {
          usePrenotaStore.getState().actions.draft.documento.update({
            chaveNFe: newValue,
          });
        }, 300);
      }
    },
    []
  );

  // Handler para quando o usuário sai do campo (onBlur)
  const handleInputBlur = useCallback(() => {
    if (inputValue.trim()) {
      // Limpa caracteres especiais da chave
      const chaveLimpa = identificacaoUtils.xml.cleanKey(inputValue);

      if (chaveLimpa !== inputValue) {
        // Atualiza o input com a chave limpa
        setInputValue(chaveLimpa);

        // Limpa timeout anterior
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Atualiza a store imediatamente com a chave limpa
        usePrenotaStore.getState().actions.draft.documento.update({
          chaveNFe: chaveLimpa,
        });
      }

      // Se a chave limpa tem 44 caracteres, faz busca automática
      // NFSE (50 dígitos) não faz busca automática, é 100% manual
      if (chaveLimpa.length === 44 && onAutoSearch) {
        console.log(
          "🔍 [useChaveInput] Chave com 44 caracteres detectada, iniciando busca automática:",
          chaveLimpa
        );
        onAutoSearch(chaveLimpa);
      }
    }
  }, [inputValue, onAutoSearch]);

  return {
    inputValue,
    setInputValue,
    handleInputChange,
    handleInputBlur,
  };
}
